/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { login } from '../core/_requests'
import { KTSVG, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { useAuth } from '../core/Auth'
import { LanguageHelp, setLanguage } from '../../../../_metronic/helpers/LanguageHelper'
import { toast } from 'react-toastify';

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email(LanguageHelp('Wrong email format'))
    .min(3, LanguageHelp('Minimum 3 symbols'))
    .max(100, LanguageHelp('Maximum 100 symbols'))
    .matches(/^[\w-]+(\.[\w-]+)*@([a-z0-9-]+(\.[a-z0-9-]+)*?\.[a-z]{2,6}|(\d{1,3}\.){3}\d{1,3})(:\d{4})?$/, LanguageHelp("Wrong email format!"))
    .required(LanguageHelp('Email is required')),
  password: Yup.string()
    .min(8, LanguageHelp('Must contain 8 characters!'))
    .max(25, LanguageHelp('Must contain no more than 25 characters!'))
    .matches(/^(?=.*[\!\@\#\$\%\^\'\&\*\_\+\=\|\;\:\,\.\~\-])/, LanguageHelp("One special character required! -> !@#$%^'&*-_+=|;:,.~"))
    .matches(/^(?=.*[0-9])/, LanguageHelp("One special character required!"))
    .matches(/^(?=.*[a-z])/, LanguageHelp("One lowercase required!"))
    .matches(/^(?=.*[A-Z])/, LanguageHelp("One uppercase required!"))
    .required(LanguageHelp('Password is required')),
  isRememberMeChecked: Yup.bool()
})

const initialValues = {
  email: '',
  password: '',
  isRememberMeChecked: false,
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const [isPasswordShow, setIsPasswordShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const { saveAuth } = useAuth()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      // alert(localStorage.getItem('aisenseus_di') as string)
      setLoading(true)
      try {
        const { data: auth } = await login(values.email.trim(), values.password, values.isRememberMeChecked, localStorage.getItem('aisenseus_di') as string)

        if (auth.httpStatusCode === 400) {
          toast.error(LanguageHelp('The login details are incorrect'));
          console.error(auth.message)
          saveAuth(undefined)
          setStatus('The login details are incorrect')
          setSubmitting(false)
          setLoading(false)
        }
        else { saveAuth(auth); setLanguage(auth.result.language, false) }

      } catch (ex) {
        if (ex instanceof Error) {
          if (ex.message === "Network Error") {
            toast.error(LanguageHelp('Our panel is temporarily offline for maintenance. Thank you for your understanding.'));
          } else {
            toast.error(LanguageHelp(ex.message));
          }
        } else {
          toast.error(LanguageHelp('An unexpected error has occurred'));
          console.log('Unexpected error', ex);
        }
        saveAuth(undefined)
        setStatus('Our panel is temporarily offline for maintenance. Thank you for your understanding.')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <div className="text-center mb-10"><h1 className="text-dark fw-bolder mb-3">{LanguageHelp('Login')}</h1></div>
        <label className='form-label fs-6 fw-bolder text-dark mb-1'>{LanguageHelp('Email')}</label>
        <input
          placeholder={LanguageHelp('Email')}
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            { 'is-invalid': formik.touched.email && formik.errors.email },
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>{LanguageHelp('Password')}</label>
        <div className="input-group mb-5">
          <input
            placeholder={LanguageHelp('Password')}
            type={isPasswordShow ? 'text' : 'password'}
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
          />
          <span className="input-group-text" onClick={(e) => { setIsPasswordShow(!isPasswordShow); }}>
            {isPasswordShow && <i style={{ cursor: "pointer", minWidth:"30px" }} className='far fa-eye-slash fs-2 text-primary'></i>}
            {!isPasswordShow && <i style={{ cursor: "pointer", minWidth:"30px" }} className='far fa-eye fs-2 text-primary'></i>}
          </span>
        </div>

        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}

        {formik.touched.password && formik.errors.password && (
          <div className='form-text'>
            {LanguageHelp('Your password should be 8-25 characters long and should contain at least one lowercase, one uppercase, one numeric digit and one special characters! !@#$%^\'&*-_+=|;:,.~')}
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Wrapper */}
      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div>
          <label className='form-check form-check-inline mt-5' htmlFor='kt_login_toc_agree'>
            <input
              className='form-check-input'
              type='checkbox'
              id='kt_login_toc_agree'
              {...formik.getFieldProps('isRememberMeChecked')}
            />
            <span>
              {LanguageHelp('Remember Me')}
            </span>
          </label>
        </div>

        {/* begin::Link */}
        <Link to='/auth/forgot-password' className='link-primary mt-5'>
          {LanguageHelp('Forgot Password ?')}
        </Link>
        {/* end::Link */}
      </div>
      {/* end::Wrapper */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>{LanguageHelp('Login')}</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              {LanguageHelp('Please Wait...')}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      <div className='text-gray-500 text-center fw-semibold fs-6'>
        {LanguageHelp('Not a Member yet?')}{' '}
        <Link to='/auth/registration' className='link-primary'>
          {LanguageHelp('Try for Free')}
        </Link>
      </div>
    </form>
  )
}